import { Injectable, inject } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { DeleteLevelSportAction, SetUserLevelSportAction } from './user-sports.actions';
import { ProfileService } from '@smatchy/sdk';
import { firstValueFrom } from 'rxjs';
import { LoadUserResourceAction } from '../../app/state/app.actions';
import { NavController } from '@ionic/angular';
import { LoadUserProfileAction } from './user-profile.actions';
import { LoadMyNextEventsAction } from '../..';
import { Store } from '@ngxs/store';
import { AnalyticsService } from '../../../services/analytics.service';
import { UserProfileSelector } from './user-profile.selector';
import { SportsSelectors } from '../../sports/state/sports.selectors';
import type { SportAnswersDto } from '@smatchy/sdk';

export interface UserSportsStateModel {
  loading: boolean;
  /** null until a level is being set, which is what the defaults set. */
  currentSportLevel: { sportId: string; level: number } | null;
}

const defaults: UserSportsStateModel = {
  loading: false,
  currentSportLevel: null,
};

@State<UserSportsStateModel>({
  name: 'userSports',
  defaults,
})
@Injectable()
export class UserSportsState {
  constructor(
    private readonly _userProfile: ProfileService,
    private readonly _store: Store,
    private readonly _analytics: AnalyticsService,
  ) {}

  router = inject(NavController);

  @Action(SetUserLevelSportAction)
  async setUserLevelSportAction(
    { patchState, dispatch }: StateContext<UserSportsStateModel>,
    { payload }: SetUserLevelSportAction,
  ) {
    patchState({
      loading: true,
      currentSportLevel: payload,
    });

    // Read before the call: afterwards the profile reload has already added it,
    // and every declaration would look like the first one.
    const declaredBefore = this._store.selectSnapshot(UserProfileSelector.profile)?.sportLevels?.length ?? 0;

    await firstValueFrom(
      this._userProfile.addOrUpdateSportLevel({
        updateMySportLevelDto: {
          sportId: payload.sportId,
          // A per-discipline sport (ski) sends which discipline this level is
          // for; the API keys the stored level on sportId + disciplineId, so the
          // four ski levels sit side by side instead of overwriting each other.
          disciplineId: payload.disciplineId,
          level: payload.level,
          /*
           * SportAnswersDto declares `answer: string`, and a numeric answer
           * from the time or distance questions is sent as a number. That has
           * always been true; `any` simply meant nobody had to look at it.
           * The API's own migration, 20260810120000, declares
           * `answer?: string | number` because the collection holds both.
           *
           * Named here, once, at the single point where the two disagree,
           * rather than coercing on the way out: changing what is sent changes
           * what is stored, and that is a decision about data.
           */
          answers: payload.answers as SportAnswersDto[],
          answerSentence: payload.answerSentence,
        },
      }),
    );

    this._analytics.logSportDeclared({
      sport: this.resolveSportSlug(payload.sportId),
      isFirstSport: declaredBefore === 0,
      source: payload.source ?? 'profile_edit',
    });

    dispatch(new LoadUserResourceAction());

    patchState({
      loading: false,
    });
  }

  @Action(DeleteLevelSportAction)
  async deleteLevelSportAction(
    { patchState, dispatch }: StateContext<UserSportsStateModel>,
    { payload }: DeleteLevelSportAction,
  ) {
    patchState({
      loading: true,
    });

    await firstValueFrom(
      this._userProfile.deleteSportLevel({
        deleteMySportLevelDto: {
          sportId: payload.sportId,
        },
      }),
    );

    dispatch(new LoadUserProfileAction());

    dispatch(new LoadMyNextEventsAction());

    patchState({
      loading: false,
    });

    this.router.navigateBack('/home/profile');
  }

  /**
   * The readable sport name, never the database id.
   *
   * In GA4 `sport` is one custom dimension, shared with group_view,
   * search_performed and the rest, which all send the short name: running,
   * tennis, badminton. sport_declared was sending a Mongo _id instead, so the
   * declared-sports report showed nothing but hex strings, and no question
   * spanning two events could be asked at all: running and
   * 677ff14e5cdfa0d74025aab4 are two different values to GA4 even when they are
   * the same sport.
   *
   * When the catalogue cannot answer, the parameter is dropped rather than
   * filled with the raw id. A missing value narrows one report; a hex value
   * poisons the dimension for every event that shares it.
   */
  private resolveSportSlug(sportId: string): string | undefined {
    const catalogue = this._store.selectSnapshot(SportsSelectors.getAllActiveSports) ?? [];
    const sport = catalogue.find((candidate) => candidate._id === sportId || candidate.id === sportId);
    return sport?.id;
  }
}
